import {
	AfterViewInit,
	Directive,
	ElementRef,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges
} from "@angular/core";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {resizeObserver$} from "../../../utils/resize.halper";
//import {debounceTime} from "rxjs/operators";

//export type HiddenItemsData= {hiddenItemNum:number;partlyHiddenItemIndex:number};
export type HorizontalHiddenItemsData= {hiddenItemsNum:number;indexOfFirstHiddenChild:number};

/**
 *  @ngModule RISCommonModule
 *  @description
 *  Returns the number of the container's child elements that are not shown due to width overflow of the content.
 *  This event get trigged if the  container width changed and or num of child element changed.
 *  getHiddenExternalItemsNum() method can be called be the code client (The directive user) if there was a change in
 *  the number of child elements some time after the view was already rendered and there was no window resize event.<br>>
 *  **note in order for this directive to work properly** -
 *  The user of this directive should take care to specify value for the @childWidth input parameter<br>
 *  All child must be at the same size
 *  .<br>
 *  regarding CSS :<br>
 *  The hosting element should have<br>
 *   display: flex;
 * 	  flex-direction: row;
 * 	  flex-wrap: wrap;
 * 	  box-sizing:border-box;
 * 	  overflow: hidden;
 * 	  height:a  value  that will ensure  that only one row of child elements will be displayed
 *  the container that is used to display the hidden items result should be defined as display:absolute<br>
 *  It is important in the event handler for the onHiddenExternalItemsNum event to call the ChangeDetectorRef.detectChanges() method to update the view.
 *  @param elementsNum the total num of the container child elements (typically should get its value from binding to a variable).
 *  @param childWidth the  width of child  item .(If margin is used it should by included in the specified  value) integer value
 *  @param colGap optional parameter use to specify spacing between the child items of the host element.integer value
 *  @param disableWidthCheck optional parameter that enable to turn on or off the hidden items num check - boolean value
 *  @param isAfterViewInitEnabled
 *  @event hiddenExternalItemsNum triggerd onAfterViewInit(of the hosting component) , when the value of the elementsNum input parameter changes
 *  ,on the hosting container element resize and on window resize.
 *  @see  https://angular.io/guide/lifecycle-hooks  for more info on onAfterViewInit
 *  @example - See FlagsComponent
 *
 */

@UntilDestroy()
@Directive({
	selector: '[cpHorizontalHiddenExternalConstWidthItemsNum]',
})
export class HorizontalHiddenExternalConstWidthItemsNumDirective implements AfterViewInit, OnDestroy,OnChanges,OnInit{

	@Input() elementsNum:number;
	@Input() childWidth:number;
	@Input() colGap:number = 0;
	@Input() disableWidthCheck:boolean;
	@Input() isAfterViewInitEnabled:boolean = true;
	@Output() onHiddenExternalItemsNum: EventEmitter<HorizontalHiddenItemsData> = new EventEmitter<HorizontalHiddenItemsData>();

	public lastTotalChildren: number;
	public prevIndexOfFirstHiddenChild:number;
	public prevContainerWidth:number = 0;
	private containerXAxisPadding:number;

	private resizeObservable$;
	//private  readonly RESIZE_OBSERVABLE_DEBOUNCE_TIME = 200;


	constructor(public elementRef: ElementRef){	}

	public ngOnInit(){
		this.lastTotalChildren = 0;
		this.prevIndexOfFirstHiddenChild = -1;
		this.resizeObservable$ = resizeObserver$(this.elementRef.nativeElement);
		this.resizeObservable$.pipe(
			//debounceTime(this.RESIZE_OBSERVABLE_DEBOUNCE_TIME),
			untilDestroyed(this)).subscribe(entries=> {
				let containerContentWidth = (entries?.length > 0)? entries[0].contentRect.width:undefined;
				if (!this.disableWidthCheck){
					this.getHiddenExternalItemsNum(containerContentWidth);
				}
		});
	}

	public ngOnChanges(changes: SimpleChanges) {
		if (!this.disableWidthCheck) {
			if (changes['elementsNum'] && !changes['elementsNum'].firstChange
				&& changes['elementsNum'].currentValue !== changes['elementsNum'].previousValue) {
				this.getHiddenExternalItemsNum(null, changes['elementsNum'].currentValue);
			}
		}
	}

	public ngAfterViewInit(): void {
		if (this.isAfterViewInitEnabled){
			let elemStyle = window.getComputedStyle(this.elementRef.nativeElement);
			this.containerXAxisPadding = parseInt(elemStyle.paddingLeft) + parseInt(elemStyle.paddingRight);
			this.getHiddenExternalItemsNum();
		}
	}

	public ngOnDestroy(): void {
	}

	public getHiddenExternalItemsNum(containerWidth?:number,numOfChildElements?:number) {
		let totalChildrenNum:number;
		let containerContentWidth:number;
		let numOfHiddenChildren = 0;
		//if items num was passed in as an argument
		if (typeof numOfChildElements !== 'undefined' && numOfChildElements !== null){
			totalChildrenNum = numOfChildElements;
		} else{
			totalChildrenNum = this.getChildCount();
		}

		if (!containerWidth){
			//pre condition the container element uses box-sizing:border-box
			//we need to do the check container padding only once
			if (this.containerXAxisPadding === undefined){
				let elemStyle = window.getComputedStyle(this.elementRef.nativeElement);
				this.containerXAxisPadding = parseInt(elemStyle.paddingLeft) + parseInt(elemStyle.paddingRight);
			}
			containerContentWidth = this.elementRef.nativeElement.clientWidth  - this.containerXAxisPadding;
		}else{
			containerContentWidth = containerWidth;
		}
		
		if (containerWidth!== this.prevContainerWidth || totalChildrenNum!== this.lastTotalChildren){

				const childWidthIncludingGap = this.childWidth + this.colGap;
				let maxDisplayedChildrenNum = containerContentWidth/(childWidthIncludingGap);
				let remainder = containerContentWidth % childWidthIncludingGap;
				maxDisplayedChildrenNum = Math.floor(maxDisplayedChildrenNum);
                
				if (maxDisplayedChildrenNum === 0){
					if ((containerContentWidth - this.childWidth) >= 0){
						maxDisplayedChildrenNum = 1;
					}
				}else{
					if (remainder>0){
						let x = maxDisplayedChildrenNum * childWidthIncludingGap;
						let y = containerContentWidth - x;
						if (y >= this.childWidth ){
							maxDisplayedChildrenNum++;
						}
					}
				}

				numOfHiddenChildren = (totalChildrenNum> maxDisplayedChildrenNum)?(totalChildrenNum - maxDisplayedChildrenNum):0;
				this.prevContainerWidth = containerContentWidth;
				this.lastTotalChildren = totalChildrenNum;
				let indexOfFirstHiddenChild = (numOfHiddenChildren > 0)?maxDisplayedChildrenNum:-1;
				this.prevIndexOfFirstHiddenChild = indexOfFirstHiddenChild;
				let retVal = {hiddenItemsNum:numOfHiddenChildren,indexOfFirstHiddenChild:indexOfFirstHiddenChild};
				this.onHiddenExternalItemsNum.emit(retVal);
				return retVal;
			}
			// if (totalChildrenNum === 0){
			// 	this.hiddenExternalItemsNum.emit({hiddenItemsNum:0,indexOfFirstHiddenChild:-1});
			// }
		



	}

	private getChildCount() {
		//return  (this.elementRef?.nativeElement?.children?.length) ?? 0;
		return  this.elementRef.nativeElement.children.length;
	}


}
